/**
 * Pergunta antes de a pessoa sair da página com alteração sem salvar.
 *
 * Três portas de saída, cada uma com seu mecanismo. O roteador do app não é o de dados, então
 * `useBlocker` não existe aqui:
 * - fechar a aba, recarregar e link comum para fora do roteador: `beforeunload` (o navegador
 *   pergunta);
 * - `<Link>`, breadcrumb, menu e qualquer `navigate()`: `history.pushState/replaceState` passam a
 *   pedir confirmação e, se a pessoa recusa, lançam `DataCloneError`, erro que o roteador repassa
 *   sem mudar de tela nem de URL;
 * - Voltar do navegador: enquanto há alteração, uma entrada sentinela (mesma URL) fica no topo do
 *   histórico. O Voltar só consome a sentinela e a pessoa continua na página; o aviso então
 *   pergunta e, recusado, recoloca a sentinela; confirmado, volta de verdade.
 *
 * O remendo e a sentinela são únicos no módulo: vários usos simultâneos do hook compartilham o
 * mesmo e o último a sair restaura o original. A sentinela que sobra depois de a alteração ser
 * salva deixa uma entrada repetida no histórico (mesma URL), que o primeiro Voltar apenas consome.
 */
import { useEffect } from 'react';

export const MENSAGEM_DE_SAIDA =
  'Você alterou respostas das declarações de EPI e ainda não salvou. Sair desta página descarta as alterações. Deseja sair?';

const MARCA_DA_SENTINELA = 'avisoDeSaidaSentinela';
const ESPERA_DO_VOLTAR_MS = 500;

let usosAtivos = 0;
let pushOriginal: History['pushState'] | null = null;
let replaceOriginal: History['replaceState'] | null = null;
let ignorarProximoPopstate = false;
let soltarIgnorar: ReturnType<typeof setTimeout> | null = null;

const estadoAtual = (): Record<string, unknown> => {
  const estado: unknown = window.history.state;
  return typeof estado === 'object' && estado !== null ? (estado as Record<string, unknown>) : {};
};

const estaNaSentinela = (): boolean => estadoAtual()[MARCA_DA_SENTINELA] === true;

const colocarSentinela = (): void => {
  if (pushOriginal === null || estaNaSentinela()) return;
  const estado = estadoAtual();
  const idx = typeof estado.idx === 'number' ? estado.idx + 1 : undefined;
  pushOriginal.call(window.history, { ...estado, idx, [MARCA_DA_SENTINELA]: true }, '', window.location.href);
};

const mudaDeEndereco = (destino: string | URL | null | undefined): boolean =>
  destino != null && new URL(String(destino), window.location.href).href !== window.location.href;

const interceptar =
  (original: History['pushState']): History['pushState'] =>
  function (this: History, ...argumentos) {
    if (mudaDeEndereco(argumentos[2]) && !window.confirm(MENSAGEM_DE_SAIDA)) {
      throw new DOMException('Navegação cancelada: há alterações sem salvar.', 'DataCloneError');
    }
    return original.apply(this, argumentos);
  };

const aoFecharAAba = (evento: BeforeUnloadEvent): void => {
  evento.preventDefault();
};

const aoVoltarOuAvancar = (): void => {
  if (ignorarProximoPopstate) {
    ignorarProximoPopstate = false;
    return;
  }
  if (estaNaSentinela()) return;
  if (!window.confirm(MENSAGEM_DE_SAIDA)) {
    colocarSentinela();
    return;
  }
  ignorarProximoPopstate = true;
  if (soltarIgnorar !== null) clearTimeout(soltarIgnorar);
  soltarIgnorar = setTimeout(() => {
    ignorarProximoPopstate = false;
  }, ESPERA_DO_VOLTAR_MS);
  window.history.go(-1);
};

const ativarGuarda = (): void => {
  usosAtivos += 1;
  if (usosAtivos > 1) return;
  pushOriginal = window.history.pushState;
  replaceOriginal = window.history.replaceState;
  window.history.pushState = interceptar(pushOriginal);
  window.history.replaceState = interceptar(replaceOriginal);
  colocarSentinela();
  window.addEventListener('beforeunload', aoFecharAAba);
  window.addEventListener('popstate', aoVoltarOuAvancar);
};

const desativarGuarda = (): void => {
  usosAtivos -= 1;
  if (usosAtivos > 0) return;
  if (pushOriginal !== null) window.history.pushState = pushOriginal;
  if (replaceOriginal !== null) window.history.replaceState = replaceOriginal;
  pushOriginal = null;
  replaceOriginal = null;
  ignorarProximoPopstate = false;
  window.removeEventListener('beforeunload', aoFecharAAba);
  window.removeEventListener('popstate', aoVoltarOuAvancar);
};

export function useAvisoDeSaida(temAlteracaoSemSalvar: boolean): void {
  useEffect(() => {
    if (!temAlteracaoSemSalvar) return;
    ativarGuarda();
    return desativarGuarda;
  }, [temAlteracaoSemSalvar]);
}
